// The sign-up call to action shown to a visitor in the tutorial.
//
// This was "Save my spot", which promised that everything you did in the demo
// came with you. It no longer does, and should not: a new account starts on an
// empty hub so the first league you see is one you actually made. So the
// button says what it does.
//
// Every one of these carries a reason, because a form that arrives with no
// explanation reads as an obstacle, and a sign-up popup that appears unasked
// confuses more than it helps.

import { useState } from 'react';
import { Button, type ButtonVariant } from '../kit/ui';
import { SignUpSheet, type SignUpReason } from '../screens/shared/SignUpSheet';

/** Looking around costs nothing; an account is for keeping things. */
export const KEEPING_REASON: SignUpReason = {
  needs: 'The tutorial needs no account. This is for when you want a real league.',
  then: 'An account is where your leagues, your team and your trophy room live. Nothing from the tutorial comes with you — you start clean, with a league you made.',
};

/** From a button that leads to league creation. */
export const START_REASON: SignUpReason = {
  needs: 'Creating a league needs an account first.',
  then: 'A league has to belong to someone, and that is you — it is where the rules, the members and the invite link live. After this you answer a few questions and get a link to send your friends.',
};

export function SignUp({
  label = 'Sign up',
  reason = KEEPING_REASON,
  size = 'sm',
  variant = 'primary',
  full = false,
}: {
  label?: string;
  reason?: SignUpReason;
  size?: 'xs' | 'sm' | 'md';
  variant?: ButtonVariant;
  full?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button icon="invite" size={size} variant={variant} full={full} onClick={() => setOpen(true)}>
        {label}
      </Button>
      {open ? (
        <SignUpSheet
          mode="sign-up"
          opened
          onClose={() => setOpen(false)}
          onDone={() => setOpen(false)}
          reason={reason}
        />
      ) : null}
    </>
  );
}
